import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { useFormContext } from "react-hook-form";
import DatePicker from "react-datepicker";

import { GetObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import {
  ALBUM_BUCKET_NAME,
  BUCKET_REGION,
  IDENTITY_POOL_ID,
} from "../../shared/constants";
import {
  CheckBoxInput,
  FormGroupLabel,
  FormItem,
  FormValueContainer,
  HintText,
  Label,
} from "../../shared/components/form/FormElements";
import { Col, Container, Row } from "react-bootstrap";
import { formInputStyles } from "../../shared/constants/styles";

import Error from "@/shared/components/form/Error";
import {
  FilterButton,
  ActionIconContainer,
  Icon,
} from "../../shared/components/TableElements";
import DocUploader from "../../shared/components/form/DocUploader";
import { toDataUrl } from "./NewDocumentForm";
import CheckboxInputComponent from "../../shared/components/form/CheckboxInputComponent";
import { colorLightGray, colorYellowNew } from "../../utils/palette";
import { getDateFnsLocale } from "../../shared/helpers";
import { CustomInput } from "../Reminders/MaintenanceReminders/MaintenanceRemindersForm";
import InfoTooltip from "../../shared/components/InfoTooltip";
import DocumentRenewalsFormPopup from "../Reminders/DocumentRenewals/DocumentRenewalsFormPopup";
import DocRenewalFromUserPopup from "../Reminders/DocumentRenewals/components/DocRenewalFromUserPopup";
import { postApi } from "../../api/getUser";
import { useSelector } from "react-redux";
import InfoPopup from "../../shared/components/InfoPopup";
import ActionPopup from "../../shared/components/ActionPopup";
import Tooltip from "../../shared/components/Tooltip";

const NewUserDocumentForm = ({
  doc,
  documents,
  setDocuments,
  type,
  activeKey,
  setActiveKey,
  docUpdated,
  setDocUpdated,
  rowData,
}) => {
  const {
    handleSubmit,
    reset,
    register,
    control,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const { roleId } = useSelector((state) => state.user);

  const status = watch("status");
  const name = watch("name");
  const role = watch("role");

  const { t } = useTranslation("common");
  const usersList = useSelector((state) => state.users.list);
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();

  const [docName, setDocName] = useState("");
  const [docFile, setDocFile] = useState("");
  const [showDocToUser, setShowDocToUser] = useState(true);
  const [fields, setFields] = useState([]);

  const [docDeleted, setDocDeleted] = useState("");
  const [hasExpiry, setHasExpiry] = useState(false);
  const [expiryDate, setExpiryDate] = useState(null);
  const [hasReminder, setHasReminder] = useState(false);
  const [docRenewalType, setDocRenewalType] = useState("add");
  const [showAddReminderPopup, setShowAddReminderPopup] = useState(false);
  const isDisabled = !status;

  const [showRenewalDeleteWarningPopup, setShowRenewalDeleteWarningPopup] =
    useState(false);

  const [disableExpiry, setDisableExpiry] = useState(false);

  const [docRenewalData, setDocRenewalData] = useState({
    docRenewalId: "",
    expiryDate: null,
    startDate: 0,
    daysThreshold: "",
    userEmail: "",
    notes: "",
    isExpiryUpdated: false, // To check if any of the renewal data has been updated
    type: "add",
    prevExpiryUnix: 0,
  });

  const onAddReminder = (expiry) => {
    if (expiry) {
      setHasReminder(true);
      if (docRenewalType === "add") {
        setDocRenewalType("edit");
      }
    } else {
      setHasReminder(false);
      setDocRenewalType("add");
    }
  };

  //To populate values on edit
  useEffect(() => {
    if (type === "edit") {
      (async () => {
        const { docName, fields, isVisible, image } = doc;
        const imageFormat = doc?.image_format;
        setDocName(docName);
        setShowDocToUser(isVisible == "1");

        let temp = [];
        if (pathname.includes("users")) {
          fields?.forEach((f, i) => {
            temp.push({
              id: Date.now() + i,
              title: f.title ? f.title : "",
              author: f.author ? f.author : "",
            });
          });
        }
        let count = 0;
        if (pathname.includes("vehicles")) {
          for (let obj of fields) {
            let [key, value] = Object.entries(obj)[0];
            temp.push({
              id: Date.now() + count,
              title: key ? key : "",
              author: value ? value : "",
            });
            count += 1;
          }
        }
        setFields(temp);

        if (pathname.includes("users")) {
          if (image) {
            if (image.extension === "txt") {
              const imgUrl = await toDataUrl(image.url);
              let ext = image?.type[0];
              setDocFile({ ...image, url: imgUrl, ext });
            } else {
              setDocFile({ ...image, ext: image.extension });
            }
          }
        }

        if (pathname.includes("vehicles")) {
          if (image?.url) {
            setDocFile({ ...image, ext: image.extension });
          } else {
            setDocFile("");
          }
        }
      })();
    }
  }, []);

  //Update expiryDate and doc renewal data on edit
  useEffect(() => {
    if (type === "edit" && usersList?.length > 0) {
      (async () => {
        const docRenewalApiData = {
          querystring: "get_single_doc_renewal",
          user_id: rowData?.user_id,
          document_name: doc.docName,
        };
        const singleDocRenewalRes = await postApi(
          docRenewalApiData,
          "commonNew"
        );
        if (singleDocRenewalRes?.success) {
          const { user_data } = singleDocRenewalRes;
          if (user_data?.length > 0) {
            setHasExpiry(true);
            setHasReminder(true);
            setDocRenewalType("edit");
            const {
              due_on_date,
              days_threshold,
              emails,
              notes,
              doc_renewal_id,
              start_date,
            } = user_data[0];

            setExpiryDate(new Date(due_on_date * 1000));

            //Emails
            let temp = [];
            if (emails) {
              const userEmails = emails.split(":::");
              if (userEmails.length > 0) {
                usersList.forEach((user) => {
                  if (userEmails.includes(user.email)) {
                    temp.push(user);
                  }
                });
              }
            }

            setDocRenewalData({
              docRenewalId: doc_renewal_id,
              expiryDate: new Date(due_on_date * 1000),
              prevExpiryUnix: due_on_date,
              startDate: start_date,
              daysThreshold: days_threshold,
              userEmail: temp,
              notes: notes || "",
              isExpiryUpdated: false,
              type: "edit",
            });
          }
        }
      })();
    }
  }, [usersList]);

  useEffect(() => {
    setDocuments((prev) =>
      prev.map((d) => {
        if (d.id === doc.id) {
          return {
            ...d,
            isVisible: showDocToUser ? "1" : "0",
            docRenewalData: { ...d.docRenewalData, isExpiryUpdated: true },
          };
        } else {
          return d;
        }
      })
    );
  }, [showDocToUser]);

  useEffect(() => {
    setDocuments((prev) =>
      prev.map((d) => {
        if (d.id === doc.id) {
          return { ...d, fields: fields };
        } else {
          return d;
        }
      })
    );
  }, [fields]);

  useEffect(() => {
    setDocuments((prev) =>
      prev.map((d) => {
        if (d.id === doc.id) {
          return {
            ...d,
            docRenewalData: docRenewalData,
          };
        } else {
          return d;
        }
      })
    );

    // if (docRenewalData?.type === "edit" && !docRenewalData?.expiryDate) {
    //   setShowRenewalDeleteWarningPopup(true);
    // }
  }, [docRenewalData]);

  useEffect(() => {
    setDocuments((prev) =>
      prev.map((d) => {
        if (d.id === doc.id) {
          return {
            ...d,
            hasExpiry: hasExpiry,
          };
        } else {
          return d;
        }
      })
    );

    // if (
    //   docRenewalData?.type === "edit" &&
    //   //!docRenewalData?.expiryDate &&
    //   !hasExpiry
    // ) {
    //   setShowRenewalDeleteWarningPopup(true);
    // }
  }, [hasExpiry]);

  useEffect(() => {
    // if (docFile) {
    //   setDocUpdated(true);
    // }
    setDocuments((prev) => {
      let temp = [...prev];
      temp = temp.map((d) => {
        if (d.id === doc.id) {
          if (docFile) {
            return {
              ...d,
              image: docFile,
              imgUpdated: true,
              docRenewalData: { ...d.docRenewalData, isExpiryUpdated: true },
            };
          } else {
            return {
              ...d,
              image: docFile,
            };
          }
        } else {
          return d;
        }
      });
      return temp;
    });
  }, [docFile]);

  useEffect(() => {
    if (!name || !docName || !docFile) {
      setDisableExpiry(true);
      //setHasExpiry(false);
    }

    if (name && docName && docFile) {
      setDisableExpiry(false);
    }
  }, [docName, docFile, name]);

  const handleAddFields = () => {
    setFields((prev) => {
      const temp = [...prev];
      temp.push({ id: Date.now(), title: "", author: "" });
      return temp;
    });
  };

  const handleDocNameChange = (e) => {
    const regex = /^[a-zA-Z0-9 ]+$/;
    if (e.target.value && !regex.test(e.target.value)) {
      return;
    }
    setDocName(e.target.value);
    setDocuments((prev) => {
      let temp = [...prev];
      temp = temp.map((d) => {
        if (d.id === doc.id) {
          return { ...doc, docName: e.target.value };
        } else {
          return d;
        }
      });
      return temp;
    });
  };

  const handleFieldChange = (e, id) => {
    const { name, value } = e.target;
    //console.log(name, value, id);
    setFields((prev) =>
      //let temp = [];
      prev.map((field) => {
        if (field.id === id) {
          return { ...field, [name]: value };
        } else {
          return field;
        }
      })
    );
  };

  const handleRemoveField = (id) => {
    setFields((prev) => prev.filter((field) => field.id !== id));
  };

  const handleDeleteDoc = () => {
    let key = 0;
    setDocuments((prev) => {
      const temp = [...prev];
      temp.forEach((d, id) => {
        if (d.id === doc.id) {
          key = id;
        }
      });
      return temp.filter((d) => d.id !== doc.id);
    });
    setActiveKey(key + 1);
    //setDocDeleted(Date.now());
  };

  const handleAddReminder = () => {
    setShowAddReminderPopup(true);
  };

  return (
    <>
      {showRenewalDeleteWarningPopup && (
        <ActionPopup
          showPopup={showRenewalDeleteWarningPopup}
          setShowPopup={setShowRenewalDeleteWarningPopup}
          message={t("user_renewals_delete_warn_msg")}
          icon={{
            className: "fi fi-rr-triangle-warning",
            color: colorYellowNew,
          }}
          handleYes={() => {
            setHasExpiry(false);
            setShowRenewalDeleteWarningPopup(false);
          }}
          handleNo={() => {
            setShowRenewalDeleteWarningPopup(false);
          }}
          yesBtnText={t("ok")}
          noBtnText={t("cancel")}
        />
      )}
      {showAddReminderPopup && (
        <DocRenewalFromUserPopup
          showPopup={showAddReminderPopup}
          setShowPopup={setShowAddReminderPopup}
          type={docRenewalType}
          docRenewalData={docRenewalData}
          setDocRenewalData={setDocRenewalData}
          docName={doc.docName}
          userName={name}
          expiry={expiryDate}
          setExpiry={setExpiryDate}
          onAddReminder={onAddReminder}
        />
      )}
      <Container>
        <Row className="my-3">
          <Col md={6}>
            <FormGroupLabel>{t("doc_name")}</FormGroupLabel>
            <FormItem
              //style={{ width: "438px" }}
              disabled={isDisabled || doc?.existingDoc}
              //tabIndex={0}
            >
              <input
                type="text"
                id="doc-name"
                name="docName"
                value={doc.docName}
                //onChange={(e) => setDocName(e.target.value)}
                onChange={handleDocNameChange}
                placeholder={t("doc_name")}
                style={formInputStyles}
                disabled={isDisabled || doc?.existingDoc}
              />
            </FormItem>

            {errors?.docName && <Error error={errors.docName.message} />}
          </Col>
        </Row>

        {(role?.value === 3 || role?.value === 4) && roleId !== 4 && (
          <Row>
            <Col className="my-1">
              {/* <FormGroupLabel style={{ height: "0.75rem" }}></FormGroupLabel> */}
              <FormValueContainer>
                <Label
                  //htmlFor="showDocToUser"
                  disabled={isDisabled}
                  //onClick={() => setShowDocToUser((prev) => !prev)}
                >
                  <CheckBoxInput
                    type="checkbox"
                    id="showDocToUser"
                    name="showDocToUser"
                    //value="showDocToUser"
                    checked={showDocToUser}
                    onChange={(e) => setShowDocToUser((prev) => !prev)}
                    style={{ marginRight: "0.5rem" }}
                    disabled={isDisabled}
                  />

                  {role?.value === 3
                    ? t("make_doc_available_to_operator")
                    : t("make_doc_available_to_tech")}
                </Label>
              </FormValueContainer>
            </Col>
          </Row>
        )}
        <Row>
          <Col className="my-3">
            {/* <FormGroupLabel style={{ height: "0.75rem" }}></FormGroupLabel> */}
            <DocUploader
              docFile={docFile}
              setDocFile={setDocFile}
              documents={documents}
              setDocuments={setDocuments}
              currentDoc={doc}
              isDisabled={isDisabled}
            />
          </Col>
        </Row>

        <Row className="my-3">
          <Col md={6}>
            <div
              className="p-3"
              style={{
                border: `1px solid ${colorLightGray}`,
                borderRadius: "5px",
              }}
            >
              {/* <CheckboxInputComponent
                label={t("doc_has_expiry_date")}
                id="has-expiry"
                name="has-expiry"
                checked={hasExpiry}
                setChecked={setHasExpiry}
                disabled={isDisabled || !name || !doc?.docName || !docFile}
              /> */}
              {disableExpiry ? (
                <Tooltip text={t("expiry_disabled_msg")}>
                  <Label style={{ cursor: "not-allowed" }} disabled={true}>
                    <CheckBoxInput
                      type="checkbox"
                      id="has-expiry"
                      name="has-expiry"
                      checked={hasExpiry}
                      // onChange={(e) => {
                      //   if (docRenewalData?.type === "edit" && hasExpiry) {
                      //     setShowRenewalDeleteWarningPopup(true);
                      //   } else {
                      //     setHasExpiry((prev) => !prev);
                      //   }
                      // }}
                      style={{ marginRight: "0.5rem" }}
                      disabled={true}
                    />

                    {t("doc_has_expiry_date")}
                  </Label>
                </Tooltip>
              ) : (
                <Label
                  //htmlFor="showDocToUser"
                  disabled={isDisabled || disableExpiry}
                  //onClick={() => setShowDocToUser((prev) => !prev)}
                >
                  <CheckBoxInput
                    type="checkbox"
                    id="has-expiry"
                    name="has-expiry"
                    //value="showDocToUser"
                    checked={hasExpiry}
                    onChange={(e) => {
                      if (hasReminder && hasExpiry) {
                        setShowRenewalDeleteWarningPopup(true);
                      } else {
                        setHasExpiry((prev) => !prev);
                      }
                    }}
                    style={{ marginRight: "0.5rem" }}
                    disabled={isDisabled || disableExpiry}
                  />

                  {t("doc_has_expiry_date")}
                </Label>
              )}

              {hasExpiry && (
                <>
                  <Row className="my-2">
                    <Col md={6}>
                      <FormGroupLabel>{t("expiry_date")}</FormGroupLabel>

                      <DatePicker
                        id="expiry-datepicker"
                        locale={getDateFnsLocale()}
                        dateFormat={"P"}
                        dropdownMode="scroll"
                        //minDate={new Date()}
                        minDate={(() => {
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          return tomorrow;
                        })()}
                        showMonthDropdown
                        showYearDropdown
                        selected={expiryDate}
                        onChange={(e) => {
                          setExpiryDate(e);
                          setDocRenewalData((prev) => ({
                            ...prev,
                            expiryDate: e,
                            isExpiryUpdated: true,
                          }));
                        }}
                        customInput={<CustomInput />}
                        placeholderText={t("date_tv")}
                        disabled={disableExpiry}
                      />
                    </Col>
                  </Row>
                  <Row className="my-2">
                    <Col>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {/* <InfoTooltip tooltipText={t("expiry_date_tooltip")} /> */}
                          <Icon className="fi fi-rr-info"></Icon>
                          <HintText>
                            {hasReminder
                              ? t("reminder_settings_configured")
                              : t("no_reminder_set")}
                            {/* {t("no_reminder_set")} */}
                          </HintText>
                        </div>
                        <FilterButton
                          type="button"
                          onClick={handleAddReminder}
                          disabled={!expiryDate || disableExpiry}
                        >
                          {docRenewalType === "add"
                            ? t("add_reminder")
                            : t("edit_reminder")}
                        </FilterButton>
                      </div>
                    </Col>
                  </Row>
                  {hasReminder && (
                    <Row className="my-2">
                      <Col>
                        <div className="d-flex align-items-center">
                          <Icon className="fi fi-rr-calendar-day"></Icon>
                          <HintText>
                            {t("reminded_before_doc_expires_msg")}
                          </HintText>
                        </div>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </div>
          </Col>
        </Row>

        <div>
          {doc.updatedDate && (
            <Row>
              <Col md={3}>
                <FormGroupLabel className="m-0">
                  {t("updated_date")}
                </FormGroupLabel>
              </Col>
              <Col md={3}>
                <p>{doc.updatedDate}</p>
              </Col>
            </Row>
          )}
          {/* <Row>
            <Col xs={6} className="my-2">
              <hr/>
            </Col>
          </Row> */}
          {fields.length > 0 && (
            <Row>
              <Col md={3}>
                <FormGroupLabel>{t("detail_title")}</FormGroupLabel>
              </Col>
              <Col md={3}>
                <FormGroupLabel>{t("detail")}</FormGroupLabel>
              </Col>
            </Row>
          )}
          {fields?.map((field) => {
            return (
              <React.Fragment key={field.id}>
                <Row className="d-flex align-items-center my-3">
                  <Col xs={3} className="d-flex align-items-center gap-2">
                    <FormItem
                      //style={{ width: "160px" }}
                      disabled={isDisabled}
                      //tabIndex={0}
                    >
                      <input
                        type="text"
                        name="title"
                        id="field-title"
                        value={field.title}
                        style={formInputStyles}
                        onChange={(e) => handleFieldChange(e, field.id)}
                        disabled={isDisabled}
                      />
                    </FormItem>
                  </Col>

                  <Col xs={3} className="d-flex align-items-center gap-2 ">
                    <FormItem
                      //style={{ width: "160px" }}
                      disabled={isDisabled}
                      //tabIndex={0}
                    >
                      <input
                        type="text"
                        name="author"
                        id="field-author"
                        value={field.author}
                        style={formInputStyles}
                        onChange={(e) => handleFieldChange(e, field.id)}
                        disabled={isDisabled}
                      />
                    </FormItem>
                  </Col>
                  {!isDisabled && (
                    <Col>
                      <ActionIconContainer
                        //style={{ fontSize: "18px", top: "1px" }}
                        onClick={() => handleRemoveField(field.id)}
                      >
                        <i
                          className="fi fi-rr-cross-small"
                          //style={{ fontSize: "16px" }}
                        />
                      </ActionIconContainer>
                    </Col>
                  )}
                </Row>
              </React.Fragment>
            );
          })}
        </div>
        {!isDisabled && (
          <>
            <Row>
              <Col className="my-3">
                <FilterButton type="button" onClick={handleAddFields}>
                  + {t("add_details")}
                </FilterButton>
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <hr className="m-0" />
              </Col>
            </Row>
            <Row>
              <Col className="my-3">
                <FilterButton type="button" onClick={handleDeleteDoc}>
                  {t("remove_doc")}
                </FilterButton>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </>
  );
};

export default NewUserDocumentForm;
